import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config";
import { useMerchantStatus } from "@/hooks/use-merchant-status";
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { AddressSummary, Avatar, ButtonLink, Card, Heading, Icon, PageContainer, Price, Skeleton, Stack, Surface, Text } from "@/components/ui";
import { Clock, Market, Motorcycle, Storefront, Wallet } from "@/icons";
import { cn } from "@/lib/cn";
import { formatBRL } from "@/lib/format-brl";
import { minDeliveryFee } from "@/pages/cart/checkout/components/service-area";
import { BasicInfo, Service } from "@/types";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

export function HeaderMenu({
  basicInfo,
  deliveryService
}: {
  basicInfo: BasicInfo,
  deliveryService?: Service
}) {

  const { merchantIsOpen, nextOpeningMessage } = useMerchantStatus()
  const { header } = useStorefrontAppearance()
  const scheduling = useStorefrontScheduling()
  const acceptsScheduling = scheduling.known && scheduling.schedulableChannels.length > 0

  return (
    <div className="relative z-sticky overflow-hidden pt-header shadow-card">
      {header.showBanner && (
        <div className="overflow-hidden aspect-wide w-full">
          <div className="absolute -z-raised aspect-wide w-full m-auto overflow-hidden">
            <Skeleton shape="flat" className="absolute inset-0" />
            {basicInfo?.bannerImage?.URL ? (
              <Image
                width={1200}
                height={400}
                alt="banner"
                className="relative w-full h-full object-cover bg-card"
                src={basicInfo?.bannerImage?.URL}
                priority
                sizes="(max-width: 900px) 100vw, 900px"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full">
                <NoImage width={300} height={212} className="relative border-none w-full h-full" />
              </div>
            )}
          </div>
        </div>
      )}

      {header.density === "compact" ? (
        <CompactStoreInfo
          basicInfo={basicInfo}
          showBanner={header.showBanner}
          open={merchantIsOpen}
          nextOpeningMessage={nextOpeningMessage}
          acceptsScheduling={acceptsScheduling}
        />
      ) : (
        <DetailedStoreInfo
          basicInfo={basicInfo}
          showBanner={header.showBanner}
          deliveryService={deliveryService}
          open={merchantIsOpen}
          nextOpeningMessage={nextOpeningMessage}
          acceptsScheduling={acceptsScheduling}
        />
      )}
    </div>
  )
}

function StoreLogo({
  basicInfo,
  showBanner,
  className,
}: {
  basicInfo: BasicInfo
  showBanner: boolean
  className?: string
}) {
  const logoUrl = basicInfo?.logoImage?.URL
  const placement = cn(showBanner && "-mt-14", className)
  return logoUrl ? (
    <Avatar size="2xl" surface="card" src={logoUrl} alt="logo" data-testid="store-logo" className={placement} />
  ) : (
    <Avatar size="2xl" surface="card" icon={Storefront} data-testid="store-logo" className={placement} />
  )
}

/**
 * Compact header. Card surface and logo bite share one flex row so negative
 * logo margin climbs the banner without dragging the surface. Context-line
 * order flips with width on purpose: status first on wide rows, last when
 * stacked on mobile.
 */
function CompactStoreInfo({
  basicInfo,
  showBanner,
  open,
  nextOpeningMessage,
  acceptsScheduling,
}: {
  basicInfo: BasicInfo
  showBanner: boolean
  open: boolean
  nextOpeningMessage?: string | null
  acceptsScheduling?: boolean
}) {
  return (
    <Surface surface="card" className={cn("pb-4", !showBanner && "pt-4")}>
      <PageContainer
        className={cn(
          "flex flex-col items-center sm:flex-row sm:gap-6",
          showBanner ? "sm:items-start" : "sm:items-center",
        )}
      >
        <StoreLogo basicInfo={basicInfo} showBanner={showBanner} className={cn(showBanner && "sm:self-start")} />

        <div
          className={cn(
            "flex w-full min-w-0 flex-col items-center gap-1 pt-2 text-center sm:w-auto sm:flex-1 sm:items-start sm:text-left",
            !showBanner && "sm:pt-0",
          )}
        >
          <Heading level={1} size="title" truncate className="max-w-full">
            {basicInfo.name}
          </Heading>

          <div className="flex flex-col items-center gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
            <StoreStatusText
              open={open}
              nextOpeningMessage={closedStatusDetail(nextOpeningMessage, acceptsScheduling)}
              className="order-last sm:order-none"
            />

            <div className="flex max-w-full items-center gap-3 sm:contents">
              <AddressSummary
                data-testid="store-address-row"
                address={{ city: basicInfo.address?.city }}
                variant="compact"
                showIcon
              />
              <ButtonLink to="/info" variant="plain" tone="primary" size="auto" weight="normal" className="shrink-0">
                Ver mais
              </ButtonLink>
            </div>
          </div>
        </div>
      </PageContainer>
    </Surface>
  )
}

/** Detailed header: the storefront's historical layout. */
function DetailedStoreInfo({
  basicInfo,
  showBanner,
  deliveryService,
  open,
  nextOpeningMessage,
  acceptsScheduling,
}: {
  basicInfo: BasicInfo
  showBanner: boolean
  deliveryService?: Service
  open: boolean
  nextOpeningMessage?: string | null
  acceptsScheduling?: boolean
}) {
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)
  const deliveryEstimatePrice = useMemo(
    () => minDeliveryFee(deliveryService?.serviceArea, deliveryAreaConfig),
    [deliveryService, deliveryAreaConfig],
  )


  const identity = (
    <div className="flex w-full items-end justify-between gap-3 py-4">
      <Heading level={1} size="title" truncate className="min-w-0 flex-1">
        {basicInfo.name}
      </Heading>
      <ButtonLink to="/info" variant="link" tone="primary" size="auto" weight="normal" className="shrink-0">
        <Text as="span" size="lg" weight="normal" tone="inherit">
          Ver mais
        </Text>
      </ButtonLink>
    </div>
  )

  const details = (
    <div className="flex w-full flex-col gap-1 pt-2">
      <AddressSummary
        data-testid="store-address-row"
        address={{
          street: basicInfo.address?.street,
          number: basicInfo.address?.number,
          city: basicInfo.address?.city,
        }}
        variant="compact"
        showIcon
      />
      <HeaderExtras basicInfo={basicInfo} deliveryEstimatePrice={deliveryEstimatePrice} />
    </div>
  )

  const statusBar = (
    <PageContainer top="gap" bottom="gap">
      <Card
        variant="tinted"
        emphasis="strong"
        tone={open ? "secondary" : "danger"}
        padding="compact"
        className="flex w-full items-center justify-center gap-2"
      >
        <Icon as={Market} size="lg" tone={open ? "secondary" : "danger"} />
        <Stack align="center">
          <Text size="lg" weight="bold" tone={open ? "secondary" : "danger"}>
            {open ? "Loja aberta" : "Loja fechada"}
          </Text>
          {!open && closedStatusDetail(nextOpeningMessage, acceptsScheduling) && (
            <Text size="sm" tone="danger">
              {closedStatusDetail(nextOpeningMessage, acceptsScheduling)}
            </Text>
          )}
        </Stack>
      </Card>
    </PageContainer>
  )

  if (!showBanner) {
    return (
      <>
        <Surface surface="card" className="pt-4">
          <PageContainer className="flex flex-col items-center sm:flex-row sm:items-center sm:gap-6">
            <StoreLogo basicInfo={basicInfo} showBanner={false} />
            <div className="flex w-full min-w-0 flex-col sm:flex-1">
              {identity}
              {details}
            </div>
          </PageContainer>
        </Surface>
        <Surface surface="card" className="overflow-hidden">{statusBar}</Surface>
      </>
    )
  }

  return (
    <>
      <PageContainer>
        <StoreLogo basicInfo={basicInfo} showBanner />
      </PageContainer>
      <Surface surface="card" className="-mt-14 pt-12">
        <PageContainer>{identity}</PageContainer>
      </Surface>
      <Surface surface="card" className="overflow-hidden">
        <PageContainer>{details}</PageContainer>
        {statusBar}
      </Surface>
    </>
  )
}

function HeaderExtras({
  basicInfo,
  deliveryEstimatePrice,
}: {
  basicInfo: BasicInfo
  deliveryEstimatePrice: number
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <div className="flex items-center gap-2">
        <Icon as={Clock} size="lg" tone="primary" />
        <Text tone="muted">
          {basicInfo.averagePreparationTime
            ? `${basicInfo.averagePreparationTime} min`
            : "60 min"}
        </Text>
      </div>
      <div className="flex items-center gap-2">
        <Icon as={Motorcycle} size="lg" tone="primary" />
        <Text tone="muted">
          {deliveryEstimatePrice ? (
            <>
              A partir de <Price value={deliveryEstimatePrice} />
            </>
          ) : (
            <Price value={0} free="Entrega grátis" />
          )}
        </Text>
      </div>
      <div className="flex items-center gap-1">
        <Icon as={Wallet} size="lg" tone="primary" />
        <Text tone="muted">
          Pedido Mínimo {formatBRL(basicInfo.minOrderValue?.value ?? 0)}
        </Text>
      </div>
    </div>
  )
}

/**
 * Line under "store closed". A closed store that still accepts scheduling must
 * not announce only the close — the banner below invites scheduling.
 */
function closedStatusDetail(
  nextOpeningMessage: string | null | undefined,
  acceptsScheduling: boolean | undefined,
): string | null {
  const parts = [nextOpeningMessage, acceptsScheduling ? "aceita agendamento" : null]
  const detail = parts.filter(Boolean).join(" · ")
  return detail.length > 0 ? detail : null
}

/**
 * Compact status is TEXT, not a badge. Detailed mode uses a bar to close the
 * header block; here status is one of three context facts — a frame would
 * compete with the store name. Green open, red closed; reopen time in normal
 * weight (explanation, not verdict).
 */
function StoreStatusText({
  open,
  nextOpeningMessage,
  className,
}: {
  open: boolean
  nextOpeningMessage?: string | null
  className?: string
}) {
  return (
    <Text
      data-testid="store-status-text"
      weight="bold"
      tone={open ? "secondary" : "danger"}
      className={cn("min-w-0", className)}
    >
      {open ? "Loja aberta" : "Loja fechada"}
      {!open && nextOpeningMessage && (
        <Text as="span" weight="normal"> · {nextOpeningMessage}</Text>
      )}
    </Text>
  )
}
